import React, { useState } from 'react';
import axios from 'axios';
import './login.css';
import { useTranslation } from 'react-i18next';

// Self-serve signup (backend: POST /api/auth/logistics/signup) for
// logisticians/3PLs and e-merchants: the profile chosen here picks the steps of
// the « Mise en route » (/onboarding), where the new account lands, logged in.
const PROFILES = [
  { key: 'logistician', title: 'Logisticien / 3PL', text: 'J’expédie pour des marques' },
  { key: 'merchant', title: 'E-commerçant', text: 'Je vends en ligne' },
];

const LogisticsSignup = () => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState({
    company_name: '',
    firstname: '',
    lastname: '',
    email: '',
    phone_number: '',
    password: '',
    agent_name: '',
    business_profile: '',
  });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const setField = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.business_profile) {
      setError(t('logisticsSignup.profileRequired', 'Choisissez votre activité.'));
      return;
    }
    setIsSubmitting(true);
    try {
      const locale = (i18n.language || 'fr').startsWith('en') ? 'en' : 'fr';
      const response = await axios.post('/api/auth/logistics/signup', {
        ...form,
        agent_name: form.agent_name || null,
        locale,
      });
      localStorage.setItem('access_token', response.data.access_token);
      localStorage.setItem('shop', form.company_name);
      window.location.href = '/onboarding';
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : t('logisticsSignup.error', 'Could not create the account. Please check the form and try again.')
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-shell">
        <section className="login-brand-panel" aria-label="Unanimity AI">
          <div className="login-brand-content">
            <img src="/menu_logo.png" alt={t('logo')} className="login-brand-logo" />
            <div>
              <p className="login-eyebrow">{t('logisticsSignup.eyebrowV2', 'Pour les logisticiens et les e-commerçants')}</p>
              <h1>{t('logisticsSignup.brandTitle', 'Your AI-powered customer support, ready in minutes.')}</h1>
              <p className="login-brand-copy">
                {t(
                  'logisticsSignup.brandCopyV2',
                  'Votre espace est créé avec un agent IA prêt à répondre. Une mise en route en quelques étapes connecte ensuite vos canaux, vos transporteurs et vos données.'
                )}
              </p>
            </div>
          </div>
        </section>

        <div className="login-form">
          <div className="login-header">
            <img src="/menu_logo.png" alt={t('logo')} className="logo" />
          </div>
          <h2>{t('logisticsSignup.title', 'Create your account')}</h2>
          <form onSubmit={handleSignup}>
            <div className="form-group">
              <span className="signup-profile-label" id="signup-profile-label">{t('logisticsSignup.profile', 'Votre activité')}</span>
              <div className="signup-profile" role="radiogroup" aria-labelledby="signup-profile-label">
                {PROFILES.map(({ key, title, text }) => (
                  <button
                    key={key}
                    type="button"
                    role="radio"
                    aria-checked={form.business_profile === key}
                    className={`signup-profile-option ${form.business_profile === key ? 'is-selected' : ''}`}
                    onClick={() => { setForm((prev) => ({ ...prev, business_profile: key })); setError(''); }}
                  >
                    <strong>{t(`logisticsSignup.profiles.${key}.title`, title)}</strong>
                    <span>{t(`logisticsSignup.profiles.${key}.text`, text)}</span>
                  </button>
                ))}
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="company_name">{t('logisticsSignup.companyName', 'Company name')}</label>
              <input
                type="text"
                id="company_name"
                value={form.company_name}
                onChange={setField('company_name')}
                maxLength={120}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="firstname">{t('logisticsSignup.firstname', 'First name')}</label>
              <input type="text" id="firstname" value={form.firstname} onChange={setField('firstname')} required />
            </div>
            <div className="form-group">
              <label htmlFor="lastname">{t('logisticsSignup.lastname', 'Last name')}</label>
              <input type="text" id="lastname" value={form.lastname} onChange={setField('lastname')} required />
            </div>
            <div className="form-group">
              <label htmlFor="signup_email">{t('email')}</label>
              <input type="email" id="signup_email" value={form.email} onChange={setField('email')} required />
            </div>
            <div className="form-group">
              <label htmlFor="phone_number">{t('logisticsSignup.phone', 'Phone number (international format)')}</label>
              <input
                type="tel"
                id="phone_number"
                placeholder="+33612345678"
                value={form.phone_number}
                onChange={setField('phone_number')}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="signup_password">{t('password')}</label>
              <input
                type="password"
                id="signup_password"
                value={form.password}
                onChange={setField('password')}
                minLength={8}
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="agent_name">{t('logisticsSignup.agentName', 'AI agent name (optional)')}</label>
              <input
                type="text"
                id="agent_name"
                placeholder={t('logisticsSignup.agentNamePlaceholder', 'e.g. Camille')}
                value={form.agent_name}
                onChange={setField('agent_name')}
                maxLength={60}
              />
            </div>
            {error && (
              <div className="login-error-message" role="alert">
                {error}
              </div>
            )}
            <button type="submit" className="login-button" disabled={isSubmitting}>
              {isSubmitting
                ? t('common.loading', 'Loading')
                : t('logisticsSignup.submit', 'Create my workspace')}
            </button>
          </form>
          <a href="/">{t('logisticsSignup.alreadyAccount', 'Already have an account? Sign in')}</a>
        </div>
      </div>
    </div>
  );
};

export default LogisticsSignup;
